/**
 * Content-level validation for uploaded files.
 *
 * A browser derives `File.type` from the file's EXTENSION, not its bytes — rename 2MB of random
 * data to `.jpg` and it arrives as `image/jpeg`, passing every MIME/extension check the upload
 * modals do. The only way to tell a real document from a corrupted one is to look at the bytes:
 * every format this portal accepts starts with a fixed signature ("magic number"), and raster
 * images must additionally decode (which also catches a file that is truncated rather than
 * garbage — a valid header followed by nothing).
 *
 * Deliberately conservative: anything it cannot positively identify as broken is allowed through,
 * so a legitimate upload is never blocked by a format quirk. The backend remains the final word.
 */

const SIGNATURE_SAMPLE_BYTES = 64;

const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d]; // "%PDF-"

const extensionOf = (fileName: string): string => fileName.split('.').pop()?.toLowerCase() ?? '';

const startsWith = (bytes: Uint8Array, signature: number[]): boolean => signature.every((byte, index) => bytes[index] === byte);

/** Resolves false only when the browser itself refuses to decode the image. */
const decodesAsImage = (file: File): Promise<boolean> =>
    new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        const finish = (isValid: boolean) => {
            URL.revokeObjectURL(objectUrl);
            resolve(isValid);
        };

        image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0);
        image.onerror = () => finish(false);
        image.src = objectUrl;
    });

/**
 * @returns true when the file's contents match what its name/type claims.
 */
export const isFileContentValid = async (file: File): Promise<boolean> => {
    if (!file || file.size === 0) return false;

    const extension = extensionOf(file.name);
    const mimeType = (file.type || '').toLowerCase();

    let headerBytes: Uint8Array;

    try {
        const header = await file.slice(0, SIGNATURE_SAMPLE_BYTES).arrayBuffer();
        headerBytes = new Uint8Array(header);
    } catch {
        // Unreadable (removed, locked, permission revoked mid-pick) — treat as corrupted, same as
        // the FileReader error path the upload modals already handle.
        return false;
    }

    if (mimeType === 'application/pdf' || extension === 'pdf') {
        return startsWith(headerBytes, PDF_SIGNATURE);
    }

    // SVG is XML text, so there is no byte signature — the root element is the closest thing. The
    // declaration, comments or a DOCTYPE may come first, hence the search rather than a prefix test.
    if (mimeType === 'image/svg+xml' || extension === 'svg') {
        try {
            const head = await file.slice(0, 1024).text();

            return head.toLowerCase().includes('<svg') || head.trimStart().startsWith('<?xml');
        } catch {
            return false;
        }
    }

    if (mimeType === 'image/jpeg' || extension === 'jpg' || extension === 'jpeg') {
        if (!startsWith(headerBytes, JPEG_SIGNATURE)) return false;

        return decodesAsImage(file);
    }

    if (mimeType === 'image/png' || extension === 'png') {
        if (!startsWith(headerBytes, PNG_SIGNATURE)) return false;

        return decodesAsImage(file);
    }

    // An extension this helper doesn't know — the caller's own type check owns that decision.
    return true;
};
